import { Injectable } from '@angular/core';
import {
  ActiveOrderDsService,
  ActiveOrderPhaseDsService,
  ContainersDsService,
  EmployeeRegistrationDsService
} from '@app/core/data-services';
import {
  ActiveProductionOrder,
  ArticleDescription,
  CreateManualPalletData,
  EmployeeInfo,
  KpiValue,
  RunPhaseCheckoutPage,
  ProductionOrderManualModeFinishingDataViewModel,
  ProductionOrderService,
  Quantity,
  RunEmployeeWorkTimeData
} from 'chronos-core-client';
import { combineLatest, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ManualModeService {
  constructor(
    private activeOrderPhaseDsService: ActiveOrderPhaseDsService,
    private containersDsService: ContainersDsService,
    private productionOrderService: ProductionOrderService,
    private activeOrderDsService: ActiveOrderDsService,
    private employeeRegistrationDsService: EmployeeRegistrationDsService
  ) {}

  public startOrderInManualMode(productionOrderId: number, orderStartTime: Date, quantityPerPallet?: number): Observable<string> {
    let quantity = null;
    if (quantityPerPallet) {
      quantity = { value: quantityPerPallet };
    }
    return this.activeOrderPhaseDsService.startOrderInManualMode(productionOrderId, orderStartTime, quantity);
  }

  public createProducedMaterialFromPool(productionOrderId: number, quantity: Quantity): Observable<void> {
    return this.containersDsService.createProducedMaterialFromPool(productionOrderId, quantity);
  }

  public changeContainerQuantity(producedMaterialId: number, quantity: Quantity): Observable<void> {
    return this.containersDsService.changeContainerQuantity(producedMaterialId, quantity);
  }

  public endManualCuttingOrder(
    productionOrderId: number,
    orderStartTime: Date,
    orderEndTime: Date,
    isInterrupted: boolean
  ): Observable<void> {
    return this.activeOrderPhaseDsService.endOrderInManualMode(
      productionOrderId,
      orderStartTime.toISOString(),
      orderEndTime.toISOString(),
      isInterrupted
    );
  }

  public endManualSortingOrder(productionOrderId: number, orderEndTime: Date, isInterrupted: boolean): Observable<void> {
    return this.activeOrderPhaseDsService.endOrderInManualMode(productionOrderId, null, orderEndTime.toISOString(), isInterrupted);
  }

  public switchManualOrder(
    runPhaseCheckoutPage: RunPhaseCheckoutPage,
    productionOrderId: number,
    producedMaterialId: number,
    orderEndTime: Date,
    orderStartTime?: Date,
    runEmployeeWorkTimes?: RunEmployeeWorkTimeData[],
    numberOfWorkers?: number,
    duration?: string
  ): Observable<void> {
    return this.containersDsService
      .switchManualOrder(
        runPhaseCheckoutPage,
        productionOrderId,
        producedMaterialId,
        orderEndTime,
        orderStartTime,
        runEmployeeWorkTimes,
        numberOfWorkers,
        duration
      )
      .pipe(switchMap(() => this.activeOrderPhaseDsService.enterFinishingPhase()));
  }

  public getManualModeFinishingData(productionOrderId: number): Observable<ProductionOrderManualModeFinishingDataViewModel> {
    return this.activeOrderPhaseDsService.getManualModeFinishingData(productionOrderId);
  }

  public saveManualModeFinishingValues(
    runPhaseCheckoutPage: RunPhaseCheckoutPage,
    productionOrderId: number,
    runEmployeeWorkTimeData?: RunEmployeeWorkTimeData[],
    numberOfWorkers?: number,
    duration?: string
  ): Observable<void> {
    return this.activeOrderPhaseDsService.saveManualModeFinishingValues(
      runPhaseCheckoutPage,
      productionOrderId,
      runEmployeeWorkTimeData,
      numberOfWorkers,
      duration
    );
  }

  public getPalletCreationData(productionOrderId: number): Observable<CreateManualPalletData> {
    return this.containersDsService.getManualContainerCreationData(productionOrderId);
  }

  public getCombinedData(
    productionOrderId: number
  ): Observable<[CreateManualPalletData, ProductionOrderManualModeFinishingDataViewModel, EmployeeInfo[]]> {
    return combineLatest([
      this.getPalletCreationData(productionOrderId),
      this.getManualModeFinishingData(productionOrderId),
      this.employeeRegistrationDsService.getEmployees()
    ]);
  }

  public mapArticleFromManualData(articleData: CreateManualPalletData): ArticleDescription {
    return {
      id: undefined,
      thickness: articleData.thickness,
      grammage: articleData.grammage,
      articleName: articleData.articleName,
      externalArticleId: articleData.externalArticleId,
      externalConfigurationId: articleData.externalConfigurationId,
      bomUnitId: articleData.bomUnitId,
      inventoryUnitId: articleData.inventoryUnitId,
      configurationName: articleData.configurationName,
      productType: undefined,
      classification: articleData.classification,
      labelAutoPrint: undefined,
      bomUnitFactor: articleData.bomUnitFactor,
      warehouseDimension: articleData.warehouseDimension
    };
  }

  public getAllKpis(): Observable<KpiValue[]> {
    const activeOrderId = this.activeOrderDsService.getActiveOrderId();
    return this.productionOrderService.getAllKpis({ productionOrderId: activeOrderId });
  }
}
